import PropTypes from "prop-types"
import React, { useContext, useEffect, useState } from "react"
import { Form, useForm } from "react-hook-form"
import { useMutation } from "react-query"

import Button from "src/components/Button"

import { declineContract, submitContract } from "src/api/Contracts/SignContract"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"
import useUnsavedChangesWarning from "src/hooks/use_unsaved_changes_warning"

import MarinaNote from "./MarinaNote"
import ReachOut from "./ReachOut"
import { SignContractWizardContext } from "./SignContractWizardContext"
import SignContractWizardNavigation from "./SignContractWizardNavigation"
import SignContractWizardStep from "./SignContractWizardStep"
import AgreementActions from "./Steps/AgreementActions"
import buildFormSubmissionData from "./buildFormSubmissionData"
import { isReviewPhase } from "./reviewPhase"

const SignContractWizard = () => {
  const {
    agreementOverview,
    contractChatUrl,
    customFieldDefinitions,
    dispatch,
    downloadPdfUrl,
    marina,
    mobileWebview,
    postSubmitUrl,
    quote,
    reachOut,
    savedPdfUrl,
    state,
    steps,
    withTemplate,
  } = useContext(SignContractWizardContext)

  const showToast = useToast()
  const tracker = useTracker()
  const form = useForm({ mode: "onBlur" })
  const { control, handleSubmit } = form

  const {
    declined,
    expired,
    currentStep,
    docusealFormCompleted,
    docusealFormSubmitted,
  } = state
  const declinedOrExpired = declined || expired

  const [submitted, setSubmitted] = useState(false)

  const {
    mutate: submitContractMutation,
    isLoading,
    data,
    error,
  } = useMutation(["submitContract", quote.id], submitContract, {
    onSuccess: () => {
      setSubmitted(true)
      window.onbeforeunload = undefined
      // Decided server-side (postSubmitUrl) — never derive this from
      // current_user in JS.
      window.location.href = postSubmitUrl
    },
  })

  useEffect(() => {
    if (data !== undefined) {
      const {
        insuranceRequired,
        registrationRequired,
        contractSigningEnablements,
      } = quote

      tracker.trackEvent("contracts_v2:agreement_submitted", {
        contract_quote_id: quote.id,
        marina_id: marina.id,
        marina_name: marina.name,
        insurance: insuranceRequired,
        registration: registrationRequired,
        additional_info:
          contractSigningEnablements && contractSigningEnablements.length > 0,
      })

      showToast("Contract submitted successfully", { type: "success" })
    }
  }, [data, dispatch, marina.id, marina.name, quote, showToast, tracker])

  const reviewFlow = agreementOverview && withTemplate
  const currentStepConfig = steps[currentStep - 1]
  const isSignStep = reviewFlow && currentStepConfig?.key === "sign"
  const reviewPhase = isSignStep && isReviewPhase(state)
  const reviewConfirmed = isSignStep && state.reviewReady

  useUnsavedChangesWarning(reviewConfirmed, submitted)

  useEffect(() => {
    if (error === null || error === undefined) return

    if (!reviewFlow) {
      showToast(error.message, { type: "error" })
      return
    }

    if (error.code === "declined") {
      dispatch({ type: "CONTRACT_DECLINED" })
    } else if (error.code === "expired") {
      dispatch({ type: "CONTRACT_EXPIRED" })
    } else {
      dispatch({
        type: "SUBMIT_FAILED",
        payload: {
          code: error.code,
          message: error.message,
          terminal: error.terminal,
          marinaPhone: error.marinaPhone,
        },
      })
    }
  }, [error, showToast, reviewFlow, dispatch])

  const handleSubmitCallback = async (data) => {
    if (reviewFlow) {
      dispatch({ type: "SUBMIT_FAILURE_CLEARED" })
    }

    const formData = buildFormSubmissionData(
      state,
      data,
      quote,
      customFieldDefinitions
    )

    submitContractMutation({ quoteId: quote.encodedId, formData })
  }

  const renderStep = () => {
    if (declined) {
      return <Declined />
    } else if (expired) {
      return <Expired marinaName={marina.name} />
    } else {
      return <SignContractWizardStep isSubmitting={isLoading} form={form} />
    }
  }

  if (agreementOverview && currentStep === 1) {
    const OverviewComponent = steps[0].component

    return (
      <div className="m-auto mb-9 mt-4 max-w-[1150px] px-4 lg:px-0">
        {declined ? (
          <Declined />
        ) : expired ? (
          <Expired marinaName={marina.name} />
        ) : (
          <OverviewComponent />
        )}
      </div>
    )
  }

  if (reviewPhase) {
    return (
      <div className="m-auto mb-9 flex max-w-[1150px] flex-col space-y-8 px-4 lg:mt-2 lg:px-0">
        <SignContractWizardNavigation isSubmitting={isLoading} />
        <Form control={control} onSubmit={handleSubmit(handleSubmitCallback)}>
          {renderStep()}
        </Form>
      </div>
    )
  }

  return (
    <div
      className={`m-auto mb-9 grid max-w-[1150px] grid-cols-7 justify-center gap-8 lg:mt-2 ${
        agreementOverview ? "px-4 lg:px-0" : ""
      }`}
    >
      <div className="col-span-7">
        <SignContractWizardNavigation isSubmitting={isLoading} />
      </div>
      <Form
        control={control}
        className="col-span-7 lg:col-span-5"
        onSubmit={handleSubmit(handleSubmitCallback)}
      >
        {renderStep()}
      </Form>
      <div className="col-span-7 flex flex-col space-y-4 lg:col-span-2">
        {!agreementOverview && (
          <div className="card flex flex-col">
            <h4 className="mt-0 font-semibold">Agreement</h4>
            <AgreementActions
              downloadPdfUrl={downloadPdfUrl}
              marina={marina}
              mobileWebview={mobileWebview}
              quoteId={quote.id}
              savedPdfUrl={savedPdfUrl}
              currentStep={steps[currentStep - 1].title}
            />
          </div>
        )}
        <ReachOut
          reachOut={reachOut}
          contractChatUrl={contractChatUrl}
          marinaId={marina.id}
          marinaName={marina.name}
          quoteId={quote.id}
        />
        <MarinaNote note={quote.notes} />
        {!agreementOverview &&
          !declinedOrExpired &&
          !docusealFormCompleted &&
          !docusealFormSubmitted && <Decline />}
      </div>
    </div>
  )
}

const Decline = () => {
  const { quote, dispatch, marina, state, steps } = useContext(
    SignContractWizardContext
  )
  const tracker = useTracker()
  const showToast = useToast()
  const { encodedId } = quote

  const {
    mutate: declineMutation,
    isLoading,
    error,
  } = useMutation(["declineContract", encodedId], declineContract, {
    onSuccess: () => {
      dispatch({ type: "CONTRACT_DECLINED" })
    },
  })

  useEffect(() => {
    if (error !== null && error !== undefined) {
      showToast(error.message, { type: "error" })
    }
  }, [error, showToast])

  const handleDecline = (event) => {
    event.preventDefault()

    const confirmDecline = window.confirm(
      "Are you sure you want to decline this contract?"
    )

    if (!confirmDecline) return

    tracker.trackEvent("contracts_v2:agreement_declined", {
      contract_quote_id: quote.id,
      marina_id: marina.id,
      marina_name: marina.name,
      current_step: steps[state.currentStep - 1].title,
    })

    declineMutation({ quoteId: encodedId })
  }

  return (
    <div className="flex flex-col space-y-4 rounded-lg border border-gray-300 px-4 py-6">
      <span>
        If you no longer wish to complete this contract, you may decline the
        agreement below.
      </span>
      <Button
        variant="tertiary"
        onClick={handleDecline}
        fullWidth={true}
        isLoading={isLoading}
      >
        Decline Agreement
      </Button>
    </div>
  )
}

const Declined = () => {
  return (
    <div className="rounded-lg border border-gray-300 p-6">
      <div className="flex items-center space-x-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
          <i className="fa-solid fa-xmark" />
        </div>
        <h4 className="m-0 font-semibold">This agreement has been declined</h4>
      </div>
    </div>
  )
}

const Expired = ({ marinaName }) => {
  return (
    <div className="rounded-lg border border-gray-300 p-6">
      <div className="flex items-center space-x-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
          <i className="fa-solid fa-xmark" />
        </div>
        <h4 className="m-0 font-semibold">
          This agreement is voided and cannot be signed. Please contact{" "}
          {marinaName} for details.
        </h4>
      </div>
    </div>
  )
}

Expired.propTypes = {
  marinaName: PropTypes.string.isRequired,
}

export default SignContractWizard
